import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Upload, CreditCard } from "lucide-react";
import { PAYMENT_URL } from "./data";
import { scrollToSection } from "@/lib/scrollTo";

const HERO_IMG =
  "https://images.pexels.com/photos/34929879/pexels-photo-34929879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const LINES = [
  { text: "Academic Excellence,", accent: false },
  { text: "Written by Hand.", accent: true },
  { text: "Delivered on Time.", accent: false },
];

const STATS = [
  { value: "15,000+", label: "Projects Completed" },
  { value: "4.9/5", label: "Student Rating" },
  { value: "<15 min", label: "Response Time" },
  { value: "24/7", label: "Live Support" },
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.33, 1, 0.68, 1], delay: 0.5 + i * 0.13 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Historic university courtyard at night" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#FAF8F2]/88" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-[linear-gradient(to_top,#FAF8F2,transparent)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-5 pb-32 pt-36 sm:px-8 sm:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-8 flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#A07E1F]"
          data-testid="hero-overline"
        >
          <span className="inline-block h-2 w-2 rotate-45 bg-[#D4AF37]" />
          Premium Academic Support · Trusted Worldwide
        </motion.p>

        <h1 className="font-serif-display max-w-4xl text-5xl font-medium leading-[1.05] tracking-tight text-[#12294D] sm:text-6xl lg:text-7xl" data-testid="hero-heading">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${line.accent ? "italic text-[#A07E1F]" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#46586F] sm:text-lg"
          data-testid="hero-subtext"
        >
          MyHomeworkAssist pairs you with degree-holding experts who deliver 100% human-written,
          plagiarism-free assignments, dissertations and coursework — for students across the UK,
          Germany, Australia and 12+ more countries.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
        >
          <button
            onClick={() => scrollToSection("#quote")}
            data-testid="hero-get-quote-button"
            className="group flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 font-mono-accent text-xs font-bold uppercase tracking-[0.18em] text-[#0A1E3C] transition-colors duration-300 hover:bg-[#C9A227]"
          >
            Get a Free Quote
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToSection("#upload-brief")}
            data-testid="hero-upload-brief-button"
            className="flex items-center justify-center gap-3 rounded-full border border-[#D4AF37]/50 px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.18em] text-[#A07E1F] transition-colors duration-300 hover:bg-[#D4AF37]/10"
          >
            <Upload size={16} /> Upload Your Brief
          </button>
          <a
            href={PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-pay-now-button"
            className="flex items-center justify-center gap-2 px-2 py-4 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#5E6E85] transition-colors duration-300 hover:text-[#A07E1F]"
          >
            <CreditCard size={14} /> Pay securely online
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-16 grid max-w-3xl grid-cols-2 gap-x-8 gap-y-6 border-t border-[#D4AF37]/15 pt-8 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif-display text-3xl font-semibold text-[#12294D]">{s.value}</p>
              <p className="mt-1 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">{s.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          className="mt-12 flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#5E6E85]"
        >
          <ShieldCheck size={14} className="text-[#A07E1F]" />
          AI-free · Plagiarism-free · Fully confidential
        </motion.div>
      </motion.div>
    </section>
  );
};
